import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * ROI Widget - Displays time savings and value generated
 * Based on UX improvement specifications
 */
const ROIWidget = ({ compact = false }) => {
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchROIData();
  }, []);

  const fetchROIData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/roi/widget`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoiData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch ROI data:', err);
      setError('Unable to load ROI metrics');
      // Set fallback data for demo
      setRoiData({
        hours_saved: 0,
        hours_saved_display: '0 hours',
        value_generated: 0,
        value_display: '$0',
        period: 'This Month',
        trend_direction: 'flat',
        trend_percentage: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-surface rounded-lg border border-line animate-pulse`}>
        <div className="h-4 bg-subtle rounded w-1/2 mb-2"></div>
        <div className="h-8 bg-subtle rounded w-3/4"></div>
      </div>
    );
  }

  if (compact) {
    const hrs = roiData?.hours_saved ? Math.round(roiData.hours_saved) : 0;
    const val = roiData?.value_display || '$0';
    const active = hrs > 0;
    return (
      <div className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-line bg-muted text-sm whitespace-nowrap">
        <span className={active ? 'text-emerald-500' : 'text-ink-subtle'}>◷</span>
        <span className={active ? 'text-ink-muted' : 'text-ink-subtle'}>
          <strong className={active ? 'text-ink' : 'text-ink-subtle'}>{hrs}h</strong> saved
        </span>
        <span className="text-ink-subtle">·</span>
        <span className={active ? 'text-ink-muted' : 'text-ink-subtle'}>
          <strong className={active ? 'text-ink' : 'text-ink-subtle'}>{val}</strong> saved
        </span>
      </div>
    );
  }

  const TrendIcon = () => {
    if (roiData?.trend_direction === 'up') {
      return <span className="text-green-500">↑</span>;
    } else if (roiData?.trend_direction === 'down') {
      return <span className="text-red-500">↓</span>;
    }
    return <span className="text-ink-subtle">→</span>;
  };

  return (
    <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
      {/* Header — calm, green accent only on the icon */}
      <div className="px-4 py-3 border-b border-line flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500/15 border border-green-500/25 text-green-400">💰</span>
          <h3 className="text-ink font-semibold">ROI Dashboard</h3>
        </div>
        <p className="text-ink-subtle text-xs">{roiData?.period || 'This Month'}</p>
      </div>

      {/* Main Stats — number carries the green emphasis, not the whole tile */}
      <div className="p-4 grid grid-cols-2 gap-3">
        {/* Hours Saved */}
        <div className="text-center p-3 rounded-lg border border-line bg-muted">
          <div className="text-3xl font-bold text-green-400">
            {roiData?.hours_saved ? Math.round(roiData.hours_saved) : 0}
          </div>
          <div className="text-sm text-ink-muted font-medium">Hours Saved</div>
          {roiData?.trend_percentage > 0 && (
            <div className="text-xs text-ink-subtle mt-1 flex items-center justify-center gap-1">
              <TrendIcon />
              {roiData.trend_percentage}% vs last week
            </div>
          )}
        </div>

        {/* Value Generated */}
        <div className="text-center p-3 rounded-lg border border-line bg-muted">
          <div className="text-3xl font-bold text-green-400">
            {roiData?.value_display || '$0'}
          </div>
          <div className="text-sm text-ink-muted font-medium">Value Generated</div>
          <div className="text-xs text-ink-subtle mt-1">
            Based on ${49}/hr rate
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-4 pb-4 grid grid-cols-2 gap-2 text-center">
        <div className="bg-muted border border-line rounded-lg p-2">
          <div className="text-sm font-semibold text-ink">Per Search</div>
          <div className="text-xs text-ink-subtle">~2.5 hrs saved</div>
        </div>
        <div className="bg-muted border border-line rounded-lg p-2">
          <div className="text-sm font-semibold text-ink">Per 100 CVs</div>
          <div className="text-xs text-ink-subtle">~5 hrs saved</div>
        </div>
      </div>

      {/* Footer Tip */}
      <div className="bg-muted px-4 py-2 border-t border-line">
        <p className="text-xs text-ink-subtle text-center">
          Each AI search saves ~2 hours of manual work
        </p>
      </div>
    </div>
  );
};

/**
 * ROI Banner - Compact banner for search results and other pages
 */
export const ROIBanner = ({ searchTime, resultsCount }) => {
  // Calculate estimated manual time
  const manualHours = 2.5; // Hours for manual search
  const savedHours = manualHours - (searchTime / 60); // searchTime in seconds

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">⚡</span>
          <div>
            <p className="text-sm font-medium text-green-800">
              Found {resultsCount} candidates in {searchTime.toFixed(1)}s
            </p>
            <p className="text-xs text-green-600">
              Manual time: ~{manualHours} hours | You saved {savedHours.toFixed(1)} hours
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold text-green-600">
            ${(savedHours * 49).toFixed(0)}
          </span>
          <p className="text-xs text-green-500">value saved</p>
        </div>
      </div>
    </div>
  );
};

/**
 * ROI Tooltip - Hover tooltip explaining ROI impact
 */
export const ROITooltip = ({ children, message }) => {
  return (
    <div className="relative group inline-block">
      {children}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
        {message}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};

export default ROIWidget;
